import styled, { CSSObject } from "@emotion/styled";
import isPropValid from "@emotion/is-prop-valid";
import { theme } from "@/config/theme";
import { DESKTOP_HEADER_MEDIA_QUERY, MOBILE_HEADER_MEDIA_QUERY } from "./HeaderConstants";
import Typography from "@digital-hig/mui/Typography";

interface DropdownProps {
  isOpen: boolean;
  constrainHeight?: boolean;
  maxConstrainedHeight?: string | number;
  displayAsSlideOutOnMobile?: boolean;
}

export const Dropdown = styled("div", {
  shouldForwardProp: (prop) => isPropValid(prop) && prop !== "displayAsSlideOutOnMobile"
})<DropdownProps>(({ isOpen, constrainHeight = false, maxConstrainedHeight = 20, displayAsSlideOutOnMobile = false }) => ({
  zIndex: 10,
  position: "absolute",
  left: 0,
  top: 0,
  background: theme.primitives.colors.primary.white,
  borderTopLeftRadius: theme.primitives.borderRadii.none,
  borderTopRightRadius: theme.primitives.borderRadii.none,
  borderBottomLeftRadius: theme.primitives.borderRadii.medium,
  borderBottomRightRadius: theme.primitives.borderRadii.medium,
  display: isOpen ? "block" : "none",

  // HACK: This is fragile and should be replaced with something more robust.
  '&[data-dropdown-contains-grid="true"]': {
    "[data-make-bold-if-dropdown-contains-grid]": {
      [DESKTOP_HEADER_MEDIA_QUERY]: {
        fontWeight: 600
      }
    },
    "[data-increase-size-if-dropdown-contains-grid]": {
      [DESKTOP_HEADER_MEDIA_QUERY]: {
        padding: `${theme.primitives.spacings[4]} ${theme.primitives.spacings[3]}`
      },

      [`@media(min-width: ${theme.primitives.grid.scale[6]})`]: {
        width: 250
      }
    }
  },

  [MOBILE_HEADER_MEDIA_QUERY]: {
    // boxShadow: displayAsSlideOutOnMobile
    //   ? "none"
    //   : theme.tokens.effects["elevation-0-dropdown"].boxShadow,
    width: displayAsSlideOutOnMobile ? "100%" : "initial",
    height: displayAsSlideOutOnMobile ? "100%" : "initial",
    overflowY: displayAsSlideOutOnMobile ? "auto" : "initial"
  },

  [DESKTOP_HEADER_MEDIA_QUERY]: {
    // boxShadow: theme.tokens.effects["elevation-0-dropdown"].boxShadow,
    top: "calc(100% - 1px)",
    zIndex: -1,

    // These properties are used when the header is sticky.
    maxHeight: constrainHeight ? maxConstrainedHeight : "initial",
    overflowY: "auto",
    overflowX: "hidden",
    scrollbarColor: "rgb(210,210,210) rgb(46,54,69) !important",
    scrollbarWidth: "thin",
    "::-webkit-scrollbar": {
      WebkitAppearance: "none",
      width: 7
    },

    "::-webkit-scrollbar-thumb": {
      borderRadius: 4,
      backgroundColor: theme.primitives.colors.tint["slate-450"],
      WebkitBoxShadow: "0 0 1px rgba(255,255,255,.5)"
    }
  }
}));

type DirectionProps = { direction: "row" | "column" };

export const DropdownMenu = styled("ul", {
  shouldForwardProp: (prop) => isPropValid(prop) && prop !== "direction"
})<DirectionProps>(({ direction }) => ({
  margin: 0,
  paddingLeft: 1,
  paddingRight: 1,
  paddingTop: theme.primitives.spacings[3],
  paddingBottom: theme.primitives.spacings[4],
  display: "flex",
  listStyle: "none",
  flexDirection: "column",
  "&:focus": {
    outline: "none"
  },
  [DESKTOP_HEADER_MEDIA_QUERY]: {
    flexDirection: direction
  }
}));

export const DropdownMenuItem = styled.li({});

function buildDropdownMenuItemChildStyles(additionalStyles: CSSObject = {}) {
  return function ({ displayAsSlideOutOnMobile = false }: { displayAsSlideOutOnMobile?: boolean }) {
    return {
      fontFamily: theme.primitives.fontFamilies.default,
      fontSize: theme.primitives.fontSizes[2],
      color: theme.primitives.colors.primary.black,
      fontWeight: theme.primitives.fontWeights.default,
      minWidth: 220,
      maxWidth: 300,
      height: "100%",
      textDecoration: "none",
      display: "block",
      paddingTop: theme.primitives.spacings[2],
      paddingBottom: theme.primitives.spacings[2],
      paddingLeft: displayAsSlideOutOnMobile ? theme.primitives.spacings[6] : theme.primitives.spacings[3],
      paddingRight: theme.primitives.spacings[1],
      lineHeight: "normal",
      margin: 0,
      "&:hover": {
        background: theme.primitives.colors.tint["slate-100"]
      },
      "&:focus-visible": {
        outline: `2px solid ${theme.primitives.colors.accent.iris}`,
        outlineOffset: "-2px"
      },

      [DESKTOP_HEADER_MEDIA_QUERY]: {
        '&[data-drupal-menu-classname~="menu-ideas-image"]:before': {
          content: '""',
          display: "block",
          height: "7.25rem",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          marginBottom: theme.primitives.spacings[4]
        }

        // '&[data-drupal-menu-classname~="menu-ideas-viewer"]:before': {
        //   backgroundImage: `url(${viewerImageUrl})`,
        // },

        // '&[data-drupal-menu-classname~="menu-ideas-recap"]:before': {
        //   backgroundImage: `url(${photoTo3dImageUrl})`,
        // },
      },
      ...additionalStyles
    };
  };
}

const dropdownMenuItemSettings = {
  shouldForwardProp: (prop: string) => isPropValid(prop) && !["displayAsSlideOutOnMobile", "as"].includes(prop)
};

export const DropdownProfileSection = styled(
  "div",
  dropdownMenuItemSettings
)(
  buildDropdownMenuItemChildStyles({
    borderBottom: `1px solid ${theme.primitives.colors.detail["slate-light"]}`,
    paddingBottom: theme.primitives.spacings[5],
    paddingRight: theme.primitives.spacings[6],
    marginBottom: theme.primitives.spacings[3],
    minWidth: "auto",
    width: "100%",
    maxWidth: "100%",
    "&:hover": {
      background: "none"
    }
  })
);

export const DropdownMenuItemDescription = styled(Typography, {
  shouldForwardProp: (prop) => isPropValid(prop) && prop !== "hideOnMobile"
})(({ hideOnMobile = false }: { hideOnMobile?: boolean }) => ({
  paddingTop: theme.primitives.spacings[1],
  color: theme.primitives.colors.tint["slate-600"],
  letterSpacing: theme.primitives.letterSpacings.loose,
  fontSize: 12,
  display: hideOnMobile ? "none" : "block",
  [DESKTOP_HEADER_MEDIA_QUERY]: {
    display: "block"
  }
}));

DropdownMenuItemDescription.defaultProps = {
  variant: "smallprint",
  component: "span"
};

export const DropdownMenuItemButton = styled(
  "button",
  dropdownMenuItemSettings
)(
  buildDropdownMenuItemChildStyles({
    appearance: "none",
    border: "none",
    background: "none",
    textAlign: "start",
    width: "100%"
  })
);
